import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { useQuery } from "@tanstack/react-query";
import client from "shared/utils/axios-config";
import { QueryToggleOptions, User, UserParams } from "shared/types";
import { StaffListType } from "shared/types/enum";
import { LoginCredentials } from "hooks/queries/customer";

/* eslint-disable react-hooks/rules-of-hooks */
// `staff_type` is the role to list, or KARYAWAN_STAFF_TYPE for every employee
// (instructor + non-teaching staff) on the Karyawan page.
const getAllStaffByBusinessId = (
  staff_type: StaffListType,
  userParams?: UserParams,
  options?: QueryToggleOptions,
) => {
  return useQuery({
    queryKey: [staff_type, userParams],
    queryFn: async (): Promise<User[] | undefined> => {
      const res = await client.get(`/v1/staff/${staff_type}`, { params: userParams });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
};

// An instructor's login code (generated once at creation) + magic login link,
// so an admin/manager can re-share them via WhatsApp. Mirrors
// useGetCustomerLoginCode, including the older-server null link fallback.
// Skipped entirely when a Manager has restricted this surface's
// credentials: the server answers 403, and firing a request that can only
// fail would put a red line in the console on every profile view.
const useGetInstructorLoginCode = (staffId: string) => {
  const { isRestricted } = useAdminCapabilities();
  const restricted = isRestricted(AdminCapability.LoginCredentialsInstructor);
  return useQuery({
    queryKey: ["staff", staffId, "login-code"],
    queryFn: async (): Promise<LoginCredentials> => {
      const res = await client.get(`/v1/staff/${staffId}/login-code`);
      if (res.status === 200 && !res.data?.error) {
        return {
          code: res.data.data?.code ?? null,
          link: res.data.data?.link ?? null,
        };
      }
      return { code: null, link: null };
    },
    enabled: !!staffId && !restricted,
    staleTime: 5 * 60_000,
  });
};

export { getAllStaffByBusinessId, useGetInstructorLoginCode };
